/**
 * byo-v4-ui.js — the design's shared primitives.
 *
 * Ported one-to-one by name from `byo-core-v2.jsx` so a reviewer can diff them
 * against the JSX: Icon, Btn, Pill, Mark, ProductImage, SizeChip, Toast.
 *
 * The design writes every value as an inline style. House style here is BEM-ish
 * classes in a stylesheet (CLAUDE.md), so the literals live in
 * assets/byo-v4-builder.css under class names that mirror these components. The
 * values are unchanged; only where they are written moved. Anything genuinely
 * per-instance and computed (a tint index, a shake key) stays inline, because a
 * class cannot carry it.
 *
 * ES2020 module. The view layer is assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, render, useRef, useLayoutEffect } from './byo-v4-vendor.js';
/* The design's 24 icon paths, verbatim — byo-core-v2.jsx:135-140. */
export const ICONS = {
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  check: 'M5 12l5 5L20 7',
  chevL: 'M15 5l-7 7 7 7',
  chevR: 'M9 5l7 7-7 7',
  chevU: 'M5 15l7-7 7 7',
  chevD: 'M5 9l7 7 7-7',
  close: 'M6 6l12 12M18 6L6 18',
  bag: 'M6 8h12l1 12H5L6 8zM9 8V6a3 3 0 016 0v2',
  camera: 'M4 8h3l2-3h6l2 3h3v11H4zM12 17a3.5 3.5 0 100-7 3.5 3.5 0 000 7z',
  music: 'M9 18V6l10-2v12M9 18a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM19 16a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z',
  clock: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 7v5l3 2',
  edit: 'M4 20h4L18 10l-4-4L4 16v4z',
  menu: 'M4 7h16M4 12h16M4 17h16',
  arrowR: 'M5 12h14M13 6l6 6-6 6',
  trash: 'M5 7h14M9 7V5h6v2M7 7l1 13h8l1-13',
  tag: 'M2 2h10l9 9-10 10-9-9V2zM7 7h.01',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  box: 'M3 8l9-4 9 4v10l-9 4-9-4V8zM3 8l9 4 9-4M12 12v10',
  truck: 'M2 6h12v10H2zM14 9h4l4 4v3h-8M5 16.5a2 2 0 104 0 2 2 0 00-4 0zM15 16.5a2 2 0 104 0 2 2 0 00-4 0z',
  home: 'M4 11l8-7 8 7v9H4zM10 20v-6h4v6',
  upload: 'M12 16V5M7 10l5-5 5 5M4 20h16',
  gift: 'M4 10h16v10H4zM2 6h20v4H2zM12 6v14M12 6c-2-4-6-3-6-1s4 1 6 1m0 0c2-4 6-3 6-1s-4 1-6 1',
  help: 'M12 21a9 9 0 100-18 9 9 0 000 18zM9.5 9.5a2.5 2.5 0 015 0c0 1.6-2.5 2-2.5 3.5M12 17h.01',
  /* Not the design's: Help's "Chat" (D70) and the rewards card (D70). */
  chat: 'M7.9 20A9 9 0 1 0 4 16.1L2 22zM8 12h.01M12 12h.01M16 12h.01',
  star: 'M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3 6.4 20.2l1.1-6.2L3 9.6l6.2-.9L12 3z',
};

/** 24×24, fill none, stroke currentColor, round caps and joins, one path. */
export function Icon({ name, size = 20, stroke = 2, class: cls = '' }) {
  return html`
    <svg
      class=${`byo-v4-icon ${cls}`.trim()}
      width=${size}
      height=${size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width=${stroke}
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    ><path d=${ICONS[name]} /></svg>
  `;
}

/**
 * Heights are the design's: lg 56, md 48, sm 44 — so every variant already
 * clears the 44px minimum.
 */
export function Btn({ variant = 'primary', size = 'md', full, disabled, onClick, icon, class: cls = '', children, ...rest }) {
  return html`
    <button
      type="button"
      class=${`byo-v4-btn byo-v4-btn--${variant} byo-v4-btn--${size}${full ? ' byo-v4-btn--full' : ''} ${cls}`.trim()}
      onClick=${disabled ? undefined : onClick}
      aria-disabled=${disabled ? 'true' : undefined}
      ...${rest}
    >${children}${icon && html`<${Icon} name=${icon} size=${18} />`}</button>
  `;
}

export function Pill({ tone = 'lime', icon, class: cls = '', children }) {
  return html`
    <span class=${`byo-v4-pill byo-v4-pill--${tone} ${cls}`.trim()}>
      ${icon && html`<${Icon} name=${icon} size=${12} stroke=${2.5} />`}${children}
    </span>
  `;
}

export function Mark({ children }) {
  return html`<span class="byo-v4-mark">${children}</span>`;
}

/* The four placeholder tints, in the design's order — byo-core-v2.jsx:18. */
export const TONES = ['#fbe1d1', '#eef8c4', '#ece4e8', '#f3efe9'];

/**
 * A product's image, or the vendor's initial on one of four tints.
 *
 * The design carries a precomputed `tone` per product; ours comes from the
 * handle so it is stable across reloads and independent of list order.
 */
export function ProductImage({ p, fit = 'cover', radius = 0 }) {
  if (p.img) {
    return html`
      <img
        class="byo-v4-pimg"
        src=${p.img}
        alt=""
        loading="lazy"
        decoding="async"
        style=${{ objectFit: fit, borderRadius: radius ? `${radius}px` : undefined }}
      />
    `;
  }

  const tone = TONES[toneOf(p)];
  return html`
    <div
      class="byo-v4-pimg byo-v4-pimg--placeholder"
      style=${{ background: tone, borderRadius: radius ? `${radius}px` : undefined }}
      aria-hidden="true"
    >${(p.vendor || '?').charAt(0)}</div>
  `;
}

export function toneOf(p) {
  const key = p.handle || p.id || '';
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return hash % TONES.length;
}

/**
 * The size chip on a card. In items mode the customer never hears about
 * spaces, so it is the size name alone — DESIGN-SPEC.md §3.3.
 */
export function SizeChip({ p, itemsMode = true, slotWord, tone = 'white' }) {
  return html`<${Pill} tone=${tone}>${itemsMode ? p.size : `${p.size} · ${slotWord(p.size)}`}<//>`;
}

/**
 * The toast. Coral is reserved for errors across the whole journey
 * (byo-core-v2.jsx:19-20); nothing else uses it.
 *
 * `role="status"` is the design's. An error toast that carries an action is the
 * refusal, and the action is "Show what fits".
 */
/**
 * Layer — draws its children straight into <body>, outside every box the
 * builder and the cart sit in. DEVIATIONS.md D60: iPhone Safari clips a
 * position:fixed element to the overflow boxes around it (the builder's
 * screen frame, the stage scroller, the theme's overflow-hidden <main>), so
 * the Quick Look's close ended up behind the rail with no way out. Desktop
 * engines do not clip like that, which is why no suite ever saw it.
 *
 * Two display:contents wrappers carry the classes of the section wrapper
 * (the brand tokens) and of the `.byo` element (its colour scheme, fonts and
 * the rules scoped under .byo-v4-builder / .byo-v4-cart), so what is inside
 * styles exactly as it did in place. A hidden anchor stays in the tree to
 * find them.
 */
export function Layer({ children }) {
  const anchor = useRef(null);
  const host = useRef(null);
  useLayoutEffect(() => {
    const at = anchor.current;
    const scope = at && at.closest('.byo');
    const section = at && at.closest('.byo-v4-section');
    const outer = document.createElement('div');
    outer.className = ['byo-v4-layer', ...(section ? [...section.classList].filter((c) => c.startsWith('byo')) : [])].join(' ');
    const inner = document.createElement('div');
    inner.className = ['byo-v4-layer__scope', ...(scope ? [...scope.classList] : ['byo'])].join(' ');
    if (scope && scope.dataset.colorScheme) inner.setAttribute('data-color-scheme', scope.dataset.colorScheme);
    outer.appendChild(inner);
    document.body.appendChild(outer);
    host.current = inner;
    return () => {
      render(null, inner);
      outer.remove();
      host.current = null;
    };
  }, []);
  useLayoutEffect(() => {
    if (host.current) render(children, host.current);
  });
  return html`<span ref=${anchor} hidden data-byo-layer-anchor></span>`;
}

export function Toast({ msg }) {
  if (!msg) return null;

  const o = typeof msg === 'object' ? msg : { msg };
  const err = o.tone === 'error';

  return html`<${Layer}>
    <div
      class=${`byo-v4-toast${err ? ' byo-v4-toast--error' : ''}${o.action ? ' byo-v4-toast--action' : ''}`}
      role="status"
    >
      <span class="byo-v4-toast__msg">${o.msg}</span>
      ${o.action && html`
        <button type="button" class="byo-v4-toast__action" onClick=${o.action.onClick}>
          ${o.action.label}
        </button>
      `}
    </div>
  <//>`;
}

/**
 * Room for Safari's floating toolbar (D70).
 *
 * On iOS 26 Safari's compact tab bar floats over the foot of the page, and
 * the page runs to the foot of the screen underneath it. The build bar is the
 * last thing in a screen-high frame, so its buttons ended up under the
 * toolbar where they could not be tapped (Alan, 26 Sep 2026, a tester's
 * iPhone). The page is told nothing about the toolbar — no safe-area inset
 * without viewport-fit=cover, and innerHeight is the whole screen — so this
 * recognises the one case where it covers the page: Safari 26 or later on an
 * iPhone, upright, with the page exactly as tall as the screen. Then
 * `--byo-v4-glass` on <html> is the height the toolbar covers (77pt on an
 * iPhone 16 screenshot), and the build bar, the cart's dock and Quick Look's
 * footer keep their controls above it. Any other browser, Safari's other tab
 * layouts and landscape get 0, and nothing moves.
 */
export function glassInset() {
  const ua = navigator.userAgent || '';
  if (!/iPhone|iPod/.test(ua) || /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|DuckDuckGo/.test(ua)) return 0;
  const v = /Version\/(\d+)/.exec(ua);
  if (!v || +v[1] < 26) return 0;
  const long = Math.max(window.screen.width, window.screen.height);
  const upright = window.innerHeight > window.innerWidth;
  if (!upright || Math.abs(window.innerHeight - long) > 2) return 0;
  return long >= 812 ? 80 : 64;
}

function setGlass() {
  document.documentElement.style.setProperty('--byo-v4-glass', `${glassInset()}px`);
}
setGlass();
window.addEventListener('resize', setGlass, { passive: true });
window.addEventListener('orientationchange', setGlass);

/**
 * Wrapt Rewards points for a price in pence (D70). The rewards page's rate —
 * "Every £1 you spend earns 5 points", "A £45 box earns 225 points" — counted
 * on whole pounds, so the figure is never more than Smile.io awards. The
 * rate is the builder's "Reward points per £1" setting; 0 hides the card.
 */
export function rewardPoints(pence, config) {
  const rate = config && typeof config.pointsPerPound === 'number' ? config.pointsPerPound : 5;
  return rate > 0 ? Math.floor(Math.max(0, pence || 0) / 100) * rate : 0;
}

/**
 * PointsCard — the purple rewards card on Wrap It Up and in the cart (D70),
 * in the rewards hero's purple, as the ready-made product pages' Smile.io
 * block now is. Its wording follows that block's: "Earn … from this purchase
 * to spend on Wrapt Rewards."
 */
export function PointsCard({ points, subject = 'box', class: cls = '' }) {
  if (!points) return null;
  return html`
    <div class=${`byo-v4-points ${cls}`.trim()}>
      <${Icon} name="star" size=${18} />
      <span><strong>Earn ${points.toLocaleString('en-GB')} points</strong> with this ${subject} to spend on Wrapt Rewards.</span>
    </div>
  `;
}

/**
 * MeterText — byo-core-v2.jsx:208, verbatim in behaviour.
 *
 * Shared by Quick Look's footer (P3b) and the build bar (P4), which is why it
 * lives here with the primitives rather than in either caller.
 *
 * `short` drops the word "spaces" and the spare-capacity tail, for the
 * collapsed bar where the line has to fit beside the buttons.
 */
export function MeterText({ b, short, class: cls = '' }) {
  const fmt = window.BYO.fmtSlots;
  const words = window.BYO.Words || String;
  const state = !b.meetsMin
    ? { tone: 'orange', text: b.itemsMode ? `${words(b.need)} more to check out` : `${fmt(b.need)} more to check out` }
    : b.remaining <= 1e-9
      ? { tone: 'plum', text: 'Full', icon: 'check' }
      : /* D71: once the minimum is in, the pill says there is room for more
           rather than "Ready to check out", which read as a full box. The
           enabled Personalise button already says they can move on. */
        { tone: 'lime', text: 'Room for more', icon: 'plus' };

  const lead = b.itemsMode
    ? `${words(b.itemCount)} item${b.itemCount === 1 ? '' : 's'}`
    : `${fmt(b.slotsUsed)} of ${b.capacity}${short ? '' : ' spaces'}`;

  return html`
    <div class=${`byo-v4-meter ${cls}`.trim()}>
      <strong class="byo-v4-meter__lead">${lead}</strong>
      <${Pill} tone=${state.tone} icon=${state.icon}>${state.text}<//>
      ${!short &&
      !b.itemsMode &&
      b.meetsMin &&
      b.remaining > 1e-9 &&
      html`<span class="byo-v4-meter__spare">${fmt(b.remaining)} space${b.remaining > 1 ? 's' : ''} spare</span>`}
    </div>
  `;
}

/**
 * flyToBox — byo-core-v2.jsx:301, ported value for value.
 * DESIGN-SPEC.md §3.3 "Add animation": a 72px clone of the card's image arcs
 * to the bar's tray over 620ms, then the tray pulses 1 → 1.12 → 1 over 320ms.
 *
 * The target is `[data-byo-fly-target]`, which the build bar owns and which
 * therefore does not exist until P4. The design's own guard already returns
 * early when there is no target, so this is a no-op until the bar lands rather
 * than something that needs stubbing out and putting back.
 *
 * Honours `prefers-reduced-motion`: no ghost, no arc, no pulse.
 */
export function flyToBox(el) {
  if (!el || !el.closest) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const root = el.closest('[data-byo-screen]') || document;
  const card = el.closest('[data-byo-card]') || el.closest('[data-byo-peek]');
  const img = card && card.querySelector('img');
  const target = root.querySelector('[data-byo-fly-target]');
  if (!img || !target) return;

  const a = img.getBoundingClientRect();
  const t = target.getBoundingClientRect();
  const size = Math.min(72, a.width);

  const ghost = document.createElement('img');
  ghost.src = img.currentSrc || img.src;
  ghost.alt = '';
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${a.left + a.width / 2 - size / 2}px`,
    top: `${a.top + a.height / 2 - size / 2}px`,
    width: `${size}px`,
    height: `${size}px`,
    objectFit: 'contain',
    borderRadius: '14px',
    background: '#fff',
    boxShadow: '0 12px 28px rgba(42,22,37,.35)',
    zIndex: 9999,
    pointerEvents: 'none',
  });
  document.body.appendChild(ghost);

  const dx = t.left + t.width / 2 - (a.left + a.width / 2);
  const dy = t.top + t.height / 2 - (a.top + a.height / 2);

  const anim = ghost.animate(
    [
      { transform: 'translate(0,0) scale(1) rotate(0)', opacity: 1, offset: 0 },
      { transform: `translate(${dx * 0.45}px, ${dy * 0.35 - 60}px) scale(.9) rotate(-6deg)`, opacity: 1, offset: 0.45 },
      { transform: `translate(${dx}px, ${dy}px) scale(.2) rotate(4deg)`, opacity: 0.9, offset: 1 },
    ],
    { duration: 620, easing: 'cubic-bezier(.3,.7,.2,1)' }
  );

  anim.onfinish = () => {
    ghost.remove();
    target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], {
      duration: 320,
      easing: 'ease-out',
    });
  };
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
/**
 * Focus for overlays (D132). The theme draws a dashed ring round whatever has
 * `:focus-visible`, and iPhone Safari matches it when a sheet focuses its
 * close button just after a tap: the ring sat over the card below the X
 * (Alan, 11 Oct). So an overlay opened by a tap or click focuses itself, which
 * draws nothing; one opened from the keyboard focuses its first control, ring
 * and all. Focus going back to the opener after a tap is quiet the same way,
 * until the next key press or until it moves on.
 */
let lastByKeys = false;
window.addEventListener(
  'keydown',
  (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    lastByKeys = true;
    document.querySelectorAll('[data-byo-v4-quiet]').forEach((el) => el.removeAttribute('data-byo-v4-quiet'));
  },
  true,
);
window.addEventListener(
  'pointerdown',
  () => {
    lastByKeys = false;
  },
  true,
);

/** Focus an overlay as it opens: `first` from the keyboard, `box` itself otherwise. */
export function focusInto(box, first) {
  if (!box) return;
  if (lastByKeys && first) first.focus();
  else box.focus({ preventScroll: true });
}

/** Give focus back to what opened an overlay, without a ring after a tap. */
export function focusBack(el) {
  if (!el || !el.focus) return;
  if (!lastByKeys) {
    el.setAttribute('data-byo-v4-quiet', '');
    el.addEventListener('blur', () => el.removeAttribute('data-byo-v4-quiet'), { once: true });
  }
  el.focus({ preventScroll: true });
}

Object.assign((window.BYOV4 = window.BYOV4 || {}), { focusBack, focusInto, Btn, flyToBox, glassInset, Icon, ICONS, Layer, Mark, MeterText, Pill, PointsCard, ProductImage, rewardPoints, SizeChip, Toast, toneOf, TONES });
